import { normalizeAtlasEncoding } from './atlasText';
import { fixEncoding } from './encoding';

type CleanAITextOptions = {
  keepNewlines?: boolean;
  maxLines?: number;
};

function stripCodeFences(text: string) {
  // Remove ```lang ... ``` blocks entirely (keeps inner text when possible).
  return text.replace(/```[\s\S]*?```/g, (block) => block.replace(/```[a-zA-Z0-9_-]*\n?/g, '').replace(/```/g, ''));
}

function stripMarkdownMarkers(text: string) {
  let out = text;
  // Headings like "## Title" -> "Title"
  out = out.replace(/^\s{0,3}#{1,6}\s+/gm, '');
  // Horizontal rules "***" / "---" / "___"
  out = out.replace(/^\s*([*_ -])\1\1+\s*$/gm, '');
  // Bold/italic/underline markers
  out = out.replace(/\*\*\*([^*]+)\*\*\*/g, '$1');
  out = out.replace(/\*\*([^*]+)\*\*/g, '$1');
  out = out.replace(/\*([^*]+)\*/g, '$1');
  out = out.replace(/__([^_]+)__/g, '$1');
  out = out.replace(/_([^_]+)_/g, '$1');
  out = out.replace(/~~([^~]+)~~/g, '$1');
  // Links [text](url) -> text
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');
  return out;
}

function dedupeLines(lines: string[]) {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of lines) {
    const key = line.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(line);
  }
  return out;
}

export function cleanAIText(value: unknown, options: CleanAITextOptions = {}) {
  const keepNewlines = options.keepNewlines ?? true;
  const maxLines = typeof options.maxLines === 'number' ? options.maxLines : undefined;

  let text = String(value ?? '');
  text = fixEncoding(text);
  text = normalizeAtlasEncoding(text);
  text = text.replace(/\r\n/g, '\n');
  text = stripCodeFences(text);
  text = stripMarkdownMarkers(text);
  text = text.replace(/\u200b/g, ''); // zero-width
  text = text.replace(/[ \t]+\n/g, '\n');
  text = text.replace(/\n{3,}/g, '\n\n');
  text = text.replace(/[ \t]{2,}/g, ' ');
  text = text.trim();

  if (!keepNewlines) {
    text = text.replace(/\s*\n\s*/g, ' ').replace(/\s+/g, ' ').trim();
  }

  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const deduped = dedupeLines(lines);
  const limited = typeof maxLines === 'number' ? deduped.slice(0, Math.max(0, maxLines)) : deduped;

  // Preserve paragraph breaks when we keep newlines.
  return keepNewlines ? limited.join('\n') : limited.join(' ');
}

export function cleanAIList(values: unknown, options: CleanAITextOptions = {}) {
  if (!Array.isArray(values)) return [];
  return values
    .map((item) => cleanAIText(item, { ...options, keepNewlines: false }))
    .map((item) => String(item || '').trim())
    .filter(Boolean);
}
